import React from 'react';
import PageSection from 'components/PageSection';
import { LpFeeCommitmentStatus } from 'further-types/lp/lp-fee-template';
import useVehicle from 'hooks/lp/data/vehicle/useVehicle';
import { useLayoutContext } from 'contexts/LayoutContext';
import TransactionAllocator from 'components/TransactionAllocator';
import useTransactionTypeSelectorOptions from 'hooks/form/useTransactionTypeSelectorOptions';
import { sumBy } from 'lodash';
import PageLoader from 'components/PageLoader';
import { ApprovalSummaryData } from '..';
import { numberToCurrencyString } from 'further-ui/utils';
import Statistic from 'components/new/Statistic';
import { Column, Row } from 'further-ui/components/Layout';
import Divider from '@mui/material/Divider';
import { styled } from '@mui/material';
import IndividualFeeTable from '../FeeList/IndividualFeeTable';
import { ListLpFeesCategory } from 'further-types/lp/lp-fee';

const StyledHeaderRow = styled(Row)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.sm, theme.spacingSizes.xl),
}));

const Approval: React.FC<{
  approvalSummaryData: ApprovalSummaryData;
  insideCommitmentTransactions: ApprovalSummaryData['defaultTransactions']['insideCommitment'];
  outsideCommitmentTransactions: ApprovalSummaryData['defaultTransactions']['outsideCommitment'];
  setInsideCommitmentTransactions: (
    transactions: ApprovalSummaryData['defaultTransactions']['insideCommitment'],
  ) => void;
  setOutsideCommitmentTransactions: (
    transactions: ApprovalSummaryData['defaultTransactions']['outsideCommitment'],
  ) => void;
}> = ({
  approvalSummaryData,
  insideCommitmentTransactions,
  outsideCommitmentTransactions,
  setInsideCommitmentTransactions,
  setOutsideCommitmentTransactions,
}) => {
  const { fullFeeData, feeType, date } = approvalSummaryData;
  const { selectedVehicleId } = useLayoutContext();
  const { vehicle, isLoading: vehicleLoading } = useVehicle(selectedVehicleId);
  const { transactionTypeOptions, isLoading: transactionTypeOptionsLoading } =
    useTransactionTypeSelectorOptions();

  const { insideCommitmentFees, outsideCommitmentFees } = fullFeeData.reduce<{
    insideCommitmentFees: ApprovalSummaryData['fullFeeData'];
    outsideCommitmentFees: ApprovalSummaryData['fullFeeData'];
  }>(
    (acc, feeData) => {
      if (
        feeData.insideOutsideCommitment ===
        LpFeeCommitmentStatus.InsideCommitment
      ) {
        acc.insideCommitmentFees.push(feeData);
      } else {
        acc.outsideCommitmentFees.push(feeData);
      }
      return acc;
    },
    {
      insideCommitmentFees: [],
      outsideCommitmentFees: [],
    },
  );

  if (transactionTypeOptionsLoading || vehicleLoading || !vehicle || !feeType) {
    return <PageLoader />;
  }

  const title = ` management fees paid by ${vehicle.vehicleName} to the fund manager`;

  return (
    <>
      <Column spacing="lg">
        <Column>
          <StyledHeaderRow spacing="xl">
            <Statistic
              paddingY="sm"
              spacing="sm"
              label="Fee"
              value="Management fees"
            />
            <Divider orientation="vertical" flexItem />
            <Statistic
              paddingY="sm"
              spacing="sm"
              label="Fee date"
              value={date.toReadable()}
            />
            <Divider orientation="vertical" flexItem />
            <Statistic
              paddingY="sm"
              spacing="sm"
              label="Amount"
              value={numberToCurrencyString(
                sumBy(fullFeeData, 'feeIncTaxLcy'),
                {
                  currency: vehicle?.baseCurrency,
                },
              )}
            />
          </StyledHeaderRow>
          <Divider />
        </Column>
        <IndividualFeeTable
          currencyLcy={vehicle.baseCurrency}
          feeList={fullFeeData}
          feeType={ListLpFeesCategory.ApprovedFees}
        />
        {insideCommitmentFees.length > 0 && (
          <PageSection>
            <TransactionAllocator
              title={`Inside commitment` + title}
              vehicle={vehicle}
              transactions={insideCommitmentTransactions}
              transactionTypeOptions={transactionTypeOptions}
              transactionCurrency={vehicle.baseCurrency}
              fxRate={null}
              onChange={(transactions) => {
                setInsideCommitmentTransactions(transactions);
              }}
            />
          </PageSection>
        )}
        {outsideCommitmentFees.length > 0 && (
          <PageSection>
            <TransactionAllocator
              title={`Outside commitment` + title}
              vehicle={vehicle}
              transactions={outsideCommitmentTransactions}
              transactionTypeOptions={transactionTypeOptions}
              transactionCurrency={vehicle.baseCurrency}
              fxRate={null}
              onChange={(transactions) => {
                setOutsideCommitmentTransactions(transactions);
              }}
            />
          </PageSection>
        )}
      </Column>
    </>
  );
};

export default Approval;
